import { useRef, useState } from "react";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { HERO_IMAGE } from "@/constants/site";
import { resolveSiteAsset } from "@/lib/useSiteContent";

const CHAPTERS = [
  {
    title: "Kalite",
    text: "Her ürünü, evimizin bir üyesi için hazırlar gibi titizlikle üretiyoruz.",
  },
  {
    title: "Güven",
    text: "Şeffaf üretim anlayışımızla her iş ortaklığının arkasında duruyoruz.",
  },
  {
    title: "İş Ortaklığı",
    text: "Güçlü markalarımız ve geniş portföyümüzle sürdürülebilir değer yaratıyoruz.",
  },
];

export const ScrollStory = ({ content = {} }) => {
  const chapters = content.story_chapters?.length ? content.story_chapters : CHAPTERS;
  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const imageOpacity = useTransform(scrollYProgress, [0, 1], [0.82, 0.56]);
  const chapter = chapters[activeIndex];

  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const nextIndex = progress < 0.34 ? 0 : progress < 0.67 ? 1 : 2;
    setActiveIndex((current) => (current === nextIndex ? current : nextIndex));
  });

  return (
    <section id="hikaye" data-testid="scroll-story" className="bg-ink text-cream">
      <div ref={sectionRef} className="h-[190vh] lg:h-[220vh]">
        <div className="sticky top-0 flex h-screen items-center overflow-hidden">
          <motion.div
            className="absolute inset-0"
            style={reduceMotion ? undefined : { scale: imageScale }}
          >
            <img
              src={resolveSiteAsset(content.story_image || HERO_IMAGE.url)}
              alt={content.hero_image_alt || "Nolena Global"}
              className="h-full w-full object-cover"
            />
            <motion.div
              className="absolute inset-0 bg-ink"
              style={reduceMotion ? { opacity: 0.62 } : { opacity: imageOpacity }}
            />
          </motion.div>

          <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-end gap-8 px-5 sm:px-8 lg:grid-cols-12">
            <div className="col-span-1 pb-14 sm:pb-20 lg:col-span-8">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cream/60">
                {content.story_eyebrow || "Dostlarımız için daha iyisini üretiyoruz"}
              </p>
              <motion.div
                key={chapter.title}
                initial={reduceMotion ? false : { opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="mt-8"
              >
                <h2 className="max-w-4xl font-heading text-5xl font-bold leading-[0.95] tracking-tight text-cream sm:text-6xl xl:text-8xl">
                  {chapter.title}
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg lg:mt-7 lg:text-xl">
                  {chapter.text}
                </p>
              </motion.div>
            </div>

            <div className="col-span-1 flex justify-start pb-10 lg:col-span-4 lg:justify-end lg:pb-20">
              <div className="flex gap-3 border-t border-cream/20 pt-5 lg:block lg:space-y-3 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                {chapters.map((item, index) => (
                  <div
                    key={item.title}
                    className={`flex items-center gap-2 text-xs transition-colors duration-300 sm:text-sm lg:gap-3 ${
                      index === activeIndex ? "text-cream" : "text-cream/40"
                    }`}
                  >
                    <span
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        index === activeIndex ? "w-10 bg-brand" : "w-3 bg-cream/30"
                      }`}
                    />
                    {item.title}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
